import React, { useMemo, useState } from 'react';
import { bySymbol } from './buckets';
import { formatQuantity } from './cryptoFormat';
import { useT } from '../../i18n';

// Which bucket holds how much of each position.
//
// Every position is in the default bucket unless split here. A split names a
// bucket, a share count and, optionally, the cost of those shares; whatever is
// left stays in the default bucket. Saving replaces the symbol's whole split.

const num = (v) => {
    const text = String(v ?? '').replace(/[$,\s]/g, '');
    if (text === '') return null;
    const n = Number(text);
    return Number.isFinite(n) && n >= 0 ? n : NaN;
};

function Editor({ position, rows, buckets, defaultBucket, onSave, onCancel }) {
    const { t } = useT();
    const others = buckets.filter((b) => b.key !== defaultBucket);
    const [draft, setDraft] = useState(() => (rows.length > 0
        ? rows.map((r) => ({ bucket: r.bucket, quantity: String(r.quantity), costBasis: r.costBasis === null ? '' : String(r.costBasis) }))
        : [{ bucket: others[0]?.key ?? '', quantity: '', costBasis: '' }]));
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const held = Number(position.quantity) || 0;

    const set = (i, field) => (e) => setDraft((d) => d.map((r, j) => (j === i ? { ...r, [field]: e.target.value } : r)));

    const save = async () => {
        const parsed = draft
            .filter((r) => r.quantity !== '' || r.costBasis !== '')
            .map((r) => ({ bucket: r.bucket, quantity: num(r.quantity), costBasis: num(r.costBasis) }));
        if (parsed.some((r) => r.quantity === null || Number.isNaN(r.quantity) || Number.isNaN(r.costBasis))) {
            setError('stocks.errors.numbers'); return;
        }
        if (new Set(parsed.map((r) => r.bucket)).size !== parsed.length) { setError('stocks.errors.duplicate'); return; }
        if (parsed.reduce((s, r) => s + r.quantity, 0) > held + 1e-9) { setError('stocks.errors.tooMany'); return; }
        setBusy(true);
        const ok = await onSave(position.symbol, parsed);
        setBusy(false);
        if (!ok) setError('stocks.errors.save');
    };

    return (
        <tr className="private__row is-editing">
            <td className="is-left private__name">{position.symbol}</td>
            <td colSpan={2} className="is-left">
                {draft.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className="bucket__split" key={i}>
                        <select className="themes__select" value={r.bucket} onChange={set(i, 'bucket')} aria-label={t('stocks.bucket')}>
                            {others.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </select>
                        <input className="private__input is-right" inputMode="decimal" value={r.quantity} onChange={set(i, 'quantity')} placeholder={t('stocks.shares')} aria-label={t('stocks.shares')} />
                        <input className="private__input is-right" inputMode="decimal" value={r.costBasis} onChange={set(i, 'costBasis')} placeholder={t('stocks.costBasis')} aria-label={t('stocks.costBasis')} />
                        <button type="button" className="notes__link" onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}>
                            {t('private.removeRound')}
                        </button>
                    </div>
                ))}
                {draft.length < others.length && (
                    <button
                        type="button"
                        className="notes__link"
                        onClick={() => setDraft((d) => [...d, { bucket: others.find((b) => !d.some((r) => r.bucket === b.key))?.key ?? others[0].key, quantity: '', costBasis: '' }])}
                    >
                        {t('stocks.addSplit')}
                    </button>
                )}
                {error && <p className="holdings__error" role="alert">{t(error)}</p>}
            </td>
            <td className="private__actions">
                <div className="private__stack">
                    <button type="button" className="notes__link" onClick={save} disabled={busy}>{t('common.save')}</button>
                    <button type="button" className="notes__link" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
                </div>
            </td>
        </tr>
    );
}

export default function BucketAllocations({ positions, allocations, buckets, defaultBucket, onSave }) {
    const { t } = useT();
    const [editing, setEditing] = useState(null);
    const grouped = useMemo(() => bySymbol(allocations), [allocations]);
    const names = useMemo(() => new Map(buckets.map((b) => [b.key, b.name])), [buckets]);
    const rows = useMemo(
        () => positions.filter((p) => !p.isCash).sort((a, b) => a.symbol.localeCompare(b.symbol)),
        [positions],
    );
    if (buckets.filter((b) => b.key !== defaultBucket).length === 0) return null;

    const describe = (p) => {
        const mine = grouped.get(p.symbol) ?? [];
        const held = Number(p.quantity) || 0;
        const rest = Math.max(0, held - mine.reduce((s, a) => s + a.quantity, 0));
        return [
            ...mine.map((a) => `${names.get(a.bucket) ?? a.bucket} ${formatQuantity(a.quantity)}`),
            ...(rest > 0 ? [`${names.get(defaultBucket) ?? defaultBucket} ${formatQuantity(rest)}`] : []),
        ].join(' · ');
    };

    return (
        <div className="bucket__alloc">
            <h3 className="notes__h">{t('stocks.allocations')}</h3>
            <div className="positions__scroll">
                <table className="positions private">
                    <thead>
                        <tr>
                            <th className="is-left"><span>{t('positions.symbol')}</span></th>
                            <th className="is-right"><span>{t('positions.qty')}</span></th>
                            <th className="is-left"><span>{t('stocks.split')}</span></th>
                            <th aria-label={t('private.col.actions')} />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => (editing === p.symbol ? (
                            <Editor
                                key={p.symbol}
                                position={p}
                                rows={grouped.get(p.symbol) ?? []}
                                buckets={buckets}
                                defaultBucket={defaultBucket}
                                onCancel={() => setEditing(null)}
                                onSave={async (symbol, list) => {
                                    const ok = await onSave(symbol, list);
                                    if (ok) setEditing(null);
                                    return ok;
                                }}
                            />
                        ) : (
                            <tr key={p.symbol}>
                                <td className="is-left private__name">{p.symbol}</td>
                                <td className="is-right">{formatQuantity(Number(p.quantity))}</td>
                                <td className="is-left">{describe(p)}</td>
                                <td className="private__actions">
                                    <button
                                        type="button"
                                        className="private__edit"
                                        onClick={() => setEditing(p.symbol)}
                                        disabled={editing !== null}
                                        aria-label={t('stocks.editSplit', { symbol: p.symbol })}
                                        title={t('stocks.editSplit', { symbol: p.symbol })}
                                    >
                                        ✎
                                    </button>
                                </td>
                            </tr>
                        )))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
